import { gql, useQuery } from '@apollo/client';
import IconChevronRight from '@apollo/icons/default/IconChevronRight.svg';
import { Spinner } from '@apollo/orbit';
// TODO: Update to use Orbit's components instead
// eslint-disable-next-line no-restricted-imports
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink } from '@chakra-ui/react';
import classnames from 'classnames';
import React from 'react';
import { Link, NavLink, Route, Switch, useRouteMatch } from 'react-router-dom';

import * as orgRouteConfig from 'src/app/account/routes';
import { GlobalHeader } from 'src/app/appLayout/components/globalHeader/GlobalHeader';
import { OrgSwitcher } from 'src/components/appNav/orgSwitcher/OrgSwitcher';
import { Loading } from 'src/components/common/loading/Loading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { SuperAdminWarningBanner } from 'src/components/common/superAdminWarningBanner/SuperAdminWarningBanner';
import { DocumentTitle } from 'src/components/documentTitle/DocumentTitle';
import { PermissionBlockCard } from 'src/components/permissionGuards/permissionBlockCard/PermissionBlockCard';
import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { PlanProtectedArea } from 'src/components/planProtectedArea/PlanProtectedArea';
import { Toasts } from 'src/components/toast/Toast';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import {
  GraphLayoutQuery,
  GraphLayoutQueryVariables,
  PersistedQueryListName,
  PersistedQueryListNameVariables,
} from 'src/lib/graphqlTypes/types';

import {
  graphPersistedQueriesRouteConfig,
  graphProposalListRouteConfig,
  graphRouteConfig,
  persistedQueryListRouteConfig,
} from '../routes';
import { NonPublicGraphArea } from '../variantLayout/NonPublicGraphArea';

import { GraphNotFound } from './GraphNotFound';
import { PersistedQueriesLayout } from './persistedQueriesLayout/PersisedQueriesLayout';
import { ProposalListPage } from './proposalListPage/ProposalListPage';

const graphLayoutQuery = gql<GraphLayoutQuery, GraphLayoutQueryVariables>`
  query GraphLayoutQuery($graphId: ID!) {
    me {
      id
      ... on User {
        internalAdminRole
        memberships {
          account {
            id
          }
          user {
            id
          }
        }
      }
    }
    graph(id: $graphId) {
      id
      title
      account {
        id
      }
    }
  }
`;

const persistedQueryListNameQuery = gql<
  PersistedQueryListName,
  PersistedQueryListNameVariables
>`
  query PersistedQueryListName($graphId: ID!, $listId: ID!) {
    graph(id: $graphId) {
      id
      persistedQueryList(id: $listId) {
        id
        name
      }
    }
  }
`;

const PersistedQueryListNameComponent = ({
  graphId,
  listId,
}: {
  graphId: string;
  listId: string;
}) => {
  const { data, loading } = useQuery(persistedQueryListNameQuery, {
    variables: { graphId, listId },
  });
  return loading ? (
    <Spinner size="tiny" />
  ) : (
    <>{data?.graph?.persistedQueryList?.name ?? listId}</>
  );
};

export const GraphLayout = () => {
  const [, setCurrentAccountId] = useCurrentAccountId();
  const { graphId, listId } = useRouteParams(
    persistedQueryListRouteConfig,
    graphRouteConfig,
  );

  const { data, loading } = useQuery(graphLayoutQuery, {
    variables: { graphId },
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
  });
  const me = data?.me;
  const graph = data?.graph;
  const accountId = graph?.account?.id ?? null;

  // Only set this when accountId is truthy to avoid an infinite render
  // cycle where this nulls out `currentAccountId` and then `useIdentity`
  // sets it back to the users default org.
  React.useEffect(() => {
    if (accountId) {
      setCurrentAccountId(accountId);
    }
  }, [setCurrentAccountId, accountId]);

  const isPersistedQueriesRoute =
    useRouteMatch(graphPersistedQueriesRouteConfig.definition) !== null;
  const isProposalListRouteConfig =
    useRouteMatch(graphProposalListRouteConfig.definition) !== null;

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="relative flex flex-1 flex-col overflow-y-auto overflow-x-hidden text-primary">
      <GlobalHeader
        leftNavContext="collapsed"
        goHomeLink={
          accountId ? orgRouteConfig.org.path({ orgId: accountId }) : undefined
        }
      >
        <OrgSwitcher collapseOrgName={true} />
        {accountId && (
          <Breadcrumb
            separator={<IconChevronRight className="text-secondary" />}
            className="text-sm text-inverted"
          >
            <BreadcrumbItem>
              <BreadcrumbLink
                as={NavLink}
                to={{
                  ...orgRouteConfig.graphs.location({ orgId: accountId }),
                  state: { highlightSupergraphState: { graphId } },
                }}
              >
                {data?.graph?.title}
              </BreadcrumbLink>
            </BreadcrumbItem>
            {isPersistedQueriesRoute && (
              <BreadcrumbItem isCurrentPage={!listId}>
                <BreadcrumbLink
                  className={classnames({
                    'pointer-events-none font-semibold': !listId,
                  })}
                  as={Link}
                  to={graphPersistedQueriesRouteConfig.location({ graphId })}
                >
                  persisted query lists
                </BreadcrumbLink>
              </BreadcrumbItem>
            )}
            {listId && (
              <BreadcrumbItem
                isCurrentPage={true}
                className="text-sm font-semibold"
              >
                <PersistedQueryListNameComponent
                  listId={listId}
                  graphId={graphId}
                />
              </BreadcrumbItem>
            )}
            {isProposalListRouteConfig && (
              <BreadcrumbItem isCurrentPage={!listId}>
                <BreadcrumbLink
                  className={classnames({
                    'pointer-events-none font-semibold': !listId,
                  })}
                  as={Link}
                  to={graphProposalListRouteConfig.location({
                    graphId,
                    filterByStatuses: [
                      GraphQLTypes.ProposalStatus.OPEN,
                      GraphQLTypes.ProposalStatus.DRAFT,
                      GraphQLTypes.ProposalStatus.APPROVED,
                    ],
                  })}
                >
                  proposals
                </BreadcrumbLink>
              </BreadcrumbItem>
            )}
          </Breadcrumb>
        )}
      </GlobalHeader>
      {!data?.graph ? (
        <GraphNotFound />
      ) : (
        <div className="flex h-1 flex-1 flex-col">
          <Toasts />
          <div className="relative flex flex-1 flex-col overflow-y-auto overflow-x-hidden">
            {me?.__typename === 'User' &&
              !me.memberships.some((m) => m.account.id === accountId) &&
              me.internalAdminRole &&
              accountId && (
                // TODO(tim): would be nice to have this fetch the data it needs
                <SuperAdminWarningBanner
                  internalAdminRole={me.internalAdminRole}
                  accountId={accountId}
                />
              )}
            <DocumentTitle title={graph?.title ?? graphId}>
              <Switch>
                <Route
                  path={graphPersistedQueriesRouteConfig.definition}
                  render={() => (
                    <NonPublicGraphArea>
                      <PlanProtectedArea
                        accountId={accountId}
                        redirectExpiredToOrg={true}
                        requiresPlanCapability="persistedQueries"
                      >
                        <DocumentTitle title="Persisted Queries">
                          <PersistedQueriesLayout />
                        </DocumentTitle>
                      </PlanProtectedArea>
                    </NonPublicGraphArea>
                  )}
                />
                <Route
                  path={graphProposalListRouteConfig.definition}
                  render={() => (
                    <NonPublicGraphArea>
                      <PlanProtectedArea
                        accountId={accountId}
                        redirectExpiredToOrg={true}
                        requiresPlanCapability="proposals"
                      >
                        <DocumentTitle title="Proposals List" />
                        <PermissionGuard
                          graphId={graphId}
                          graphPermissions="canQueryProposals"
                          fallback={PermissionBlockCard}
                        >
                          <ProposalListPage />
                        </PermissionGuard>
                      </PlanProtectedArea>
                    </NonPublicGraphArea>
                  )}
                />
                <Route component={NotFound} />
              </Switch>
            </DocumentTitle>
          </div>
        </div>
      )}
    </div>
  );
};
